import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from "react";
import { Shape, Component, Category, UnifiedElement } from "@/types/diagram";
import { componentLibraryManager } from "@/lib/component.lib";
import { SVGLibraryManager } from "@/lib/svg-manager";
import useGetCategoriesService from "@/service-hooks/use-get-cagetories-service";
import useGetShapesByCategoryService from "@/service-hooks/use-get-shapes-by-category-service";
import useGetSearchedShapesService from "@/service-hooks/use-get-searched-shapes-service";
import { getShapesByName } from "@/service-hooks/use-get-shapes-by-name-service";
import { useAuthHeaders } from "@/hooks/use-auth-headers";

interface LibraryContextType {
  // State
  svgLibrary: Shape[];
  shapesByCategory: Shape[];
  components: Component[];
  categories: Category[];
  activeCategory: string;
  activeLibrary: string;
  searchQuery: string;
  searchedData: { data: UnifiedElement[]; total: number } | undefined;

  // Loading states
  isDataLoading: {
    isCategoryLoading: boolean;
    isSearchLoading: boolean;
    isShapesLoading: boolean;
  };

  // Actions
  setSvgLibrary: (shapes: Shape[]) => void;
  setComponents: (components: Component[]) => void;
  setActiveCategory: (categoryId: string) => void;
  setActiveLibrary: (libraryId: string) => void;
  setSearchQuery: (query: string) => void;
  loadLibrary: (
    libraryId: string,
    canvasSize: { width: number; height: number }
  ) => Promise<void>;
  saveComponent: (
    name: string,
    description: string,
    category: string,
    diagramComponents: any[],
    canvasSize: any,
    svgLibrary: Shape[]
  ) => Promise<Component | null>;
  deleteComponent: (componentId: string) => void;
  updateComponent: (
    componentId: string,
    updates: Partial<Omit<Component, "id" | "created">>
  ) => void;
  handleMissingDependencies: (diagramComponents: any[]) => Promise<void>;
  refetchCategories: () => void;
  refetchShapes: () => void;
  // The categories and the category's shapes are loaded only once something
  // that shows the library (the architecture canvas, the shapes tab) calls
  // this — not on every project page the provider wraps.
  ensureLibrary: () => void;
}

const LibraryContext = createContext<LibraryContextType | undefined>(undefined);

interface LibraryProviderProps {
  children: ReactNode;
}

export const LibraryProvider: React.FC<LibraryProviderProps> = ({
  children,
}) => {
  const [svgLibrary, setSvgLibrary] = useState<Shape[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [activeCategory, setActiveCategoryState] = useState<string>(() => {
    return localStorage.getItem("activeCategory") || "";
  });
  const [activeLibrary, setActiveLibraryState] = useState<string>(() => {
    const stored = localStorage.getItem("activeLibrary");
    return stored || "default";
  });
  const [searchQuery, setSearchQueryState] = useState<string>("");
  const { getAuthHeaders } = useAuthHeaders();

  const token = getAuthHeaders().Authorization.replace("Bearer ", "");

  // Off until a library consumer mounts (see ensureLibrary).
  const [libraryNeeded, setLibraryNeeded] = useState(false);
  const ensureLibrary = useCallback(() => setLibraryNeeded(true), []);

  // React Query hooks for data fetching. Same query key as the dialogs'
  // useGetCategoriesService(true), so the categories are fetched once.
  const {
    data: categories,
    isLoading: isCategoryLoading,
    refetch: refetchCategories,
  } = useGetCategoriesService(libraryNeeded);

  const { data: searchedData, isLoading: isSearchLoading } =
    useGetSearchedShapesService(searchQuery, !!searchQuery);
  const {
    data: shapesAndComponentsData,
    isLoading: isShapesLoading,
    refetch: refetchShapes,
  } = useGetShapesByCategoryService(activeCategory, libraryNeeded && !!activeCategory);

  // Set activeCategory to "All Shapes" category id once categories load
  useEffect(() => {
    if (categories?.data?.length) {
      const allShapes = categories.data.find(
        (cat) => cat.name.toLowerCase() === "all shapes"
      );
      if (allShapes) {
        setActiveCategoryState(allShapes._id);
      }
    }
  }, [categories]);

  const shapesByCategory = shapesAndComponentsData?.shapes ?? [];

  const isDataLoading = {
    isCategoryLoading,
    isSearchLoading,
    isShapesLoading,
  };

  const setActiveCategory = useCallback((categoryId: string) => {
    setActiveCategoryState(categoryId);
    localStorage.setItem("activeCategory", categoryId);
  }, []);

  const setActiveLibrary = useCallback((libraryId: string) => {
    setActiveLibraryState(libraryId);
    localStorage.setItem("activeLibrary", libraryId);
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryState(query);
  }, []);

  const loadLibrary = useCallback(
    async (
      libraryId: string,
      canvasSize: { width: number; height: number }
    ) => {
      const library = await SVGLibraryManager.getCleanLibrary(libraryId);
      if (library) {
        setSvgLibrary(library.shapes);
        componentLibraryManager.renderAllComponents(canvasSize, library.shapes);
      }
    },
    []
  );
  const saveComponent = useCallback(
    async (
      name: string,
      description: string,
      category: string,
      diagramComponents: any[],
      canvasSize: any,
      svgLibrary: Shape[]
    ): Promise<Component | null> => {
      try {
        // Check if the selectedShapes already has a component with the same name
        const existingComponent = diagramComponents.find((component) => {
          return component.source === "component" && component.shape === name;
        });

        if (existingComponent) {
          throw new Error(
            "Cannot save a diagram as a component containing the same component"
          );
        }

        const newComponent = componentLibraryManager.createComponent(
          name,
          description,
          diagramComponents,
          canvasSize,
          svgLibrary,
          true
        );

        if (newComponent) {
          componentLibraryManager.renderComponent(
            newComponent.id,
            canvasSize,
            svgLibrary
          );
          setComponents(componentLibraryManager.getAllComponents());

          // Save to server
          const { saveComponent: saveComponentService } = await import(
            "@/service-hooks/use-save-component-service"
          );
          const componentToSave = componentLibraryManager.getComponent(
            newComponent.id
          );
          if (componentToSave) {
            await saveComponentService({
              component: componentToSave,
              category,
              token,
            });
            refetchCategories();
            if (activeCategory === category) {
              refetchShapes();
            }
          }

          return newComponent;
        }
        return null;
      } catch (error) {
        console.error("Error saving component:", error);
        throw error;
      }
    },
    [activeCategory, refetchCategories, refetchShapes]
  );

  const deleteComponent = useCallback((componentId: string) => {
    try {
      componentLibraryManager.deleteComponent(componentId);
      setComponents(componentLibraryManager.getAllComponents());
    } catch (error) {
      console.error("Error deleting component:", error);
      throw error;
    }
  }, []);

  const updateComponent = useCallback(
    (
      componentId: string,
      updates: Partial<Omit<Component, "id" | "created">>
    ) => {
      try {
        componentLibraryManager.updateComponent(componentId, updates);
        setComponents(componentLibraryManager.getAllComponents());
      } catch (error) {
        console.error("Error updating component:", error);
        throw error;
      }
    },
    []
  );

  const handleMissingDependencies = useCallback(
    async (diagramComponents: any[]) => {
      const missingDependencies = new Set<string>();

      for (const element of diagramComponents) {
        if (element.source === "component") {
          if (!componentLibraryManager.getComponent(element.shape)) {
            missingDependencies.add(element.shape);
          }
        } else if (element.source === "shape") {
          const { shapesLibraryManager } = await import("@/lib/shapes-manager");
          if (!shapesLibraryManager.getShape(element.shape)) {
            missingDependencies.add(element.shape);
          }
        }

        for (const { name } of element.attached2DShapes || []) {
          const { shapesLibraryManager } = await import("@/lib/shapes-manager");
          if (!shapesLibraryManager.getShape(name)) {
            missingDependencies.add(name);
          }
        }
      }

      if (missingDependencies.size === 0) return;

      const response = await getShapesByName(
        Array.from(missingDependencies),
        token
      );
      if (!response) return;

      const { shapes = [], components = [] } = response;
      const { shapesLibraryManager } = await import("@/lib/shapes-manager");
      shapesLibraryManager.deserializeShapesLib(shapes);
      componentLibraryManager.deserializeComponentLib(components);
    },
    []
  );
  useEffect(() => {
    if (!shapesAndComponentsData) return;
    const { shapes = [], components = [] } = shapesAndComponentsData;

    setSvgLibrary(shapes);
    setComponents(components);
  }, [shapesAndComponentsData]);

  useEffect(() => {
    if (!searchedData?.data) return;
    setSvgLibrary(
      searchedData.data
        ?.filter((item) => item.type !== "COMPONENT")
        .map((item) => ({ ...item } as Shape))
    );
    setComponents(
      searchedData.data
        ?.filter((item) => item.type === "COMPONENT")
        .map((item) => ({ ...item } as Component))
    );
  }, [searchedData?.data]);

  const value: LibraryContextType = {
    // State
    svgLibrary,
    shapesByCategory,
    components,
    categories: [],
    activeCategory,
    activeLibrary,
    searchQuery,
    searchedData,
    isDataLoading,

    // Actions
    setSvgLibrary,
    setComponents,
    setActiveCategory,
    setActiveLibrary,
    setSearchQuery,
    loadLibrary,
    saveComponent,
    deleteComponent,
    updateComponent,
    handleMissingDependencies,
    refetchCategories,
    refetchShapes,
    ensureLibrary,
  };

  return (
    <LibraryContext.Provider value={value}>{children}</LibraryContext.Provider>
  );
};

export const useLibrary = (): LibraryContextType => {
  const context = useContext(LibraryContext);
  if (!context) {
    throw new Error("useLibrary must be used within a LibraryProvider");
  }
  return context;
};
